import {
  GetEsrsReportStructureQuery_,
  GetReportMetricsPerDisclosureQuery_,
  useGetReportMaterialStandardsQuery,
} from 'models';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';
import { useEffect, useState } from 'react';

type DeepTransform<T> =
  T extends Array<infer U>
    ? Array<DeepTransform<U>>
    : T extends object
      ? {
          [K in keyof T as K extends '__typename' ? never : K]: K extends 'additionalTypes'
            ? T[K] extends Array<any>
              ? DeepTransform<T[K]> | undefined
              : DeepTransform<T[K]>
            : DeepTransform<T[K]>;
        }
      : T;

// Extract ESRS standard type (without specificMFStandard)
type ESRSStandardType = DeepTransform<
  Omit<
    GetEsrsReportStructureQuery_['esrs_MaterialStandard'][number]['standard'],
    'specificMFStandard'
  >
>;

// Extract MF standard type and normalize it
type MFStandardType = DeepTransform<
  NonNullable<
    GetEsrsReportStructureQuery_['esrs_MaterialStandard'][number]['standard']['specificMFStandard']
  >
>;

// Add missing additionalTypes to MF requirements to match ESRS structure
type NormalizeMFStandard<T extends MFStandardType> = T extends {
  disclosureRequirementGroups: Array<infer Group>;
}
  ? Omit<T, 'disclosureRequirementGroups'> & {
      disclosureRequirementGroups: Array<
        Group extends { requirements: Array<infer Req> }
          ? Omit<Group, 'requirements'> & {
              requirements: Array<Req & { additionalTypes?: Array<{ additionalTypeRef: string }> }>;
            }
          : Group
      >;
    }
  : T;

// Union type that works with both structures
type UnifiedStandardType = ESRSStandardType | NormalizeMFStandard<MFStandardType>;

export type ReportStructureType = Array<{ standard: UnifiedStandardType }>;

export const useGetMetricsPerReportDisclosure = ({
  assessmentId,
  disclosureRequirementRef,
  groupMetrics,
  frameworkRef,
}: {
  assessmentId: string;
  disclosureRequirementRef: string;
  groupMetrics: string[];
  frameworkRef: string;
}) => {
  const [metrics, setMetrics] = useState<GetReportMetricsPerDisclosureQuery_>();
  const [loading, setLoading] = useState(false);
  const nhost = useNhostClient();

  useEffect(() => {
    if (assessmentId && disclosureRequirementRef) {
      setLoading(true);
      try {
        nhost.functions
          .post<GetReportMetricsPerDisclosureQuery_>(
            '/esrs/esrs-report/get-metrics-per-disclosure',
            {
              assessmentId,
              disclosureRequirementRef,
              groupMetrics,
              frameworkRef,
            }
          )
          .then((res) => {
            setLoading(false);
            setMetrics(res.body);
          });
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    }
  }, [assessmentId, disclosureRequirementRef]);
  return { data: metrics, loading };
};

export const useGetReportMaterialStandards = ({
  esrsAssessmentId,
  isClicked,
  groupMaterialMetricsRefs,
  groupMaterialStandardsRefs,
}: {
  esrsAssessmentId: string;
  isClicked: boolean;
  groupMaterialMetricsRefs: string[];
  groupMaterialStandardsRefs: string[];
}) => {
  const { data, loading } = useGetReportMaterialStandardsQuery({
    variables: {
      esrsAssessmentId,
      groupMetrics: groupMaterialMetricsRefs,
      groupStandards: groupMaterialStandardsRefs,
    },
    fetchPolicy: 'cache-first',
    skip: !esrsAssessmentId || !isClicked,
  });
  return { data, loading };
};

export const useGetEsrsReportStructure = ({
  assessmentId,
  groupStandards,
  groupMetrics,
  isCollectOnly,
}: {
  assessmentId: string;
  groupStandards: string[];
  groupMetrics: string[];
  isCollectOnly: boolean;
}) => {
  const [standards, setStandards] = useState<GetEsrsReportStructureQuery_>();
  const [loading, setLoading] = useState(false);
  const nhost = useNhostClient();

  useEffect(() => {
    if (
      assessmentId &&
      (isCollectOnly ? groupStandards.length > 0 && groupMetrics.length > 0 : true)
    ) {
      setLoading(true);
      try {
        nhost.functions
          .post<GetEsrsReportStructureQuery_>('/esrs/esrs-report/get-report-structure', {
            assessmentId,
            groupStandards,
            groupMetrics,
          })
          .then((res) => {
            setLoading(false);
            setStandards(res.body);
          });
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    }
  }, [assessmentId, isCollectOnly, groupStandards.length]);

  const data: ReportStructureType | undefined =
    standards?.esrs_MaterialStandard?.map((standard) =>
      standard.standard.specificMFStandard
        ? { standard: standard.standard.specificMFStandard }
        : standard
    ) ?? [];
  return { data, loading };
};
